import { type JSX } from "react";
import type { FlexProps } from "@chakra-ui/react";
import { Box, Flex, Link, Text } from "@chakra-ui/react";
import Logo from "assets/logos/tiger-cloud-logo.svg?react";
import { Divider } from "components/divider/Divider";
import { DitherGradient } from "components/ditherGradient/DitherGradient";

// Width the auth cards (login, signup, password reset) are laid out at, and the
// width the pages' layout reserves for them. Below that the card takes the room
// it is given.
export const AUTH_CARD_WIDTH = "559px";

export const signUpComponentStyles = {
  px: { base: "32px", md: "80px" },
  py: { base: "32px", md: "64px" },
  // The card fills the slot its page reserves for it, so its width never
  // depends on what it contains.
  w: "100%",
  maxWidth: AUTH_CARD_WIDTH,
};

const Stat = ({ value, label }: { value: string; label: string }) => (
  <Flex flexDir="column" gap="1">
    <Text
      textStyle="codeL"
      fontWeight="bold"
      textTransform="uppercase"
      letterSpacing="0.03em"
      color="fg.default"
    >
      {value}
    </Text>
    <Text textStyle="base" color="fg.muted">
      {label}
    </Text>
  </Flex>
);

const ScaleStats = () => (
  <Flex gap="4" alignItems="flex-start">
    <Stat value="3 Trillion" label="metrics / day" />
    <Divider orientation="vertical" h="11" />
    <Stat value="3 Petabytes" label="of data" />
    <Divider orientation="vertical" h="11" />
    <Stat value="1 Quadrillion" label="data points" />
  </Flex>
);

const PromoCard = () => (
  <Box
    position="relative"
    borderRadius="2xl"
    width="auto"
    flexGrow={0}
    alignSelf="flex-start"
    overflow="hidden"
    border="1px solid"
    borderColor="border.container"
  >
    <DitherGradient
      bandWidth={0.3}
      color2="dither.highlight"
      curve={0.8}
      diagonalAlignment={0.2}
      verticalAlignment={1}
      pixelSize={1.5}
      position="absolute"
      inset="0"
      zIndex="0"
      pointerEvents="none"
    />
    <Flex position="relative" zIndex="1" flexDir="column" gap="2" px="8" py="7">
      <Text
        fontSize="lg"
        fontWeight="semibold"
        lineHeight="1.5"
        color="fg.default"
      >
        Create an account and get
        <br />
        $1,000 in credit, valid for 30 days.
      </Text>
      <Text textStyle="base" color="fg.muted">
        No credit card required.
        <br />
        New accounts only.
      </Text>
    </Flex>
  </Box>
);

export const LandingCopyComponent = ({
  header,
  body,
  tagline,
  ...flexProps
}: {
  header: JSX.Element | string;
  body: JSX.Element | string;
  tagline?: string;
} & FlexProps) => {
  return (
    <Flex
      flexDir="column"
      w="442px"
      maxW="460px"
      gap="16"
      position="relative"
      hideBelow="xl"
      {...flexProps}
    >
      <Box color="fg.default">
        <Logo height={44} />
      </Box>
      <Flex flexDir="column" gap="8">
        <Text
          fontFamily="heading"
          fontSize="5xl"
          fontWeight="semibold"
          lineHeight="1.2"
          letterSpacing="-0.01em"
          color="fg.default"
        >
          {header}
        </Text>
        <Flex flexDir="column" gap="1">
          <ScaleStats />
          {tagline && (
            <Text textStyle="base" color="fg.muted">
              {tagline}
            </Text>
          )}
        </Flex>
        <Flex flexDir="column" gap="1" alignItems="flex-start">
          <Box
            color="fg.default"
            fontSize="lg"
            fontWeight="semibold"
            lineHeight="1.5"
          >
            {body}
          </Box>
          <Box textStyle="base" color="fg.muted">
            Need to host on GCP?{" "}
            <Link
              data-cy="mst-signup-link"
              href="https://www.tigerdata.com/mst-signup"
              isExternal
            >
              Sign up here
            </Link>
          </Box>
        </Flex>
      </Flex>
      <PromoCard />
    </Flex>
  );
};
